import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BookOpen, Plus, Search, Layers, Edit3, Trash2
} from 'lucide-react';

const ProgramManagement = () => {
  const [programs, setPrograms] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    pgm_name: '',
    pgm_code: '',
    dept_id: '',
    duration_years: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [editingProgram, setEditingProgram] = useState(null);

  useEffect(() => {
    fetchDepartments();
    fetchPrograms();
  }, []);

  const fetchDepartments = async () => {
    try {
      const res = await axios.get('/api/departments');
      setDepartments(res.data);
    } catch (err) {
      console.error('Failed to load departments', err);
      setMessage({ text: 'Failed to load departments for dropdown', type: 'error' });
    }
  };

  const fetchPrograms = async () => {
    try {
      const res = await axios.get('/api/programs');
      setPrograms(res.data);
    } catch (err) {
      console.error('Failed to load programs', err);
      setMessage({ text: 'Failed to load programs', type: 'error' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Validate duration_years: positive integer
    if (name === 'duration_years') {
      const num = parseInt(value, 10);
      if (value === '' || (!isNaN(num) && num > 0)) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const duration = parseInt(formData.duration_years, 10);
    if (!formData.pgm_name.trim() || !formData.pgm_code.trim() || !formData.dept_id || isNaN(duration) || duration <= 0) {
      setMessage({ text: 'Please fill all fields correctly (duration must be positive integer)', type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      if (editingProgram) {
        await axios.put(`/api/programs/${editingProgram.pgm_id}`, formData);
        setMessage({ text: 'Program updated successfully!', type: 'success' });
      } else {
        await axios.post('/api/programs', formData);
        setMessage({ text: 'Program created successfully!', type: 'success' });
      }
      setFormData({ pgm_name: '', pgm_code: '', dept_id: '', duration_years: '' });
      setEditingProgram(null);
      fetchPrograms();
    } catch (err) {
      console.error(err);
      setMessage({
        text: err.response?.data?.error || 'Failed to save program',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (prog) => {
    setEditingProgram(prog);
    setFormData({
      pgm_name: prog.pgm_name,
      pgm_code: prog.pgm_code,
      dept_id: prog.dept_id,
      duration_years: prog.duration_years.toString(),
    });
  };

  const handleDelete = async (pgmId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Are you sure you want to delete this program? This may affect related data.')) return;

    try {
      await axios.delete(`/api/programs/${pgmId}`);
      setMessage({ text: 'Program deleted successfully!', type: 'success' });
      fetchPrograms();
    } catch (err) {
      setMessage({ text: 'Failed to delete program', type: 'error' });
    }
  };

  const filteredPrograms = programs.filter((prog) =>
    prog.pgm_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prog.pgm_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (prog.dept_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(prog.duration_years).includes(searchTerm)
  );

  return (
    <div style={styles.container}>
      <div style={styles.contentWrapper}>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.iconBg}>
              <BookOpen size={28} style={styles.headerIcon} />
            </div>
            <div>
              <h1 style={styles.title}>Program Management</h1>
              <p style={styles.subtitle}>Manage academic programs for your college</p>
            </div>
          </div>
          <button
            style={styles.newBtn}
            onClick={() => {
              setEditingProgram(null);
              setFormData({ pgm_name: '', pgm_code: '', dept_id: '', duration_years: '' });
            }}
          >
            <Plus size={20} />
            {editingProgram ? 'Cancel Edit' : 'New Program'}
          </button>
        </header>

        {/* Form Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>
              <Layers size={24} />
              {editingProgram ? 'Edit Program' : 'Add New Program'}
            </h2>
          </div>

          {message.text && (
            <div
              style={{
                ...styles.message,
                ...(message.type === 'success' ? styles.successMsg : styles.errorMsg),
              }}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGrid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  Program Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="pgm_name"
                  value={formData.pgm_name}
                  onChange={handleInputChange}
                  placeholder="e.g. Bachelor of Computer Applications"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  Program Code <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="pgm_code"
                  value={formData.pgm_code}
                  onChange={handleInputChange}
                  placeholder="e.g. BCA"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  Department <span style={styles.required}>*</span>
                </label>
                <select
                  name="dept_id"
                  value={formData.dept_id}
                  onChange={handleInputChange}
                  style={styles.select}
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept.dept_id} value={dept.dept_id}>
                      {dept.dept_name} ({dept.dept_code})
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  Duration (Years) <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  name="duration_years"
                  min="1"
                  value={formData.duration_years}
                  onChange={handleInputChange}
                  placeholder="e.g. 3"
                  style={styles.input}
                  required
                />
              </div>
            </div>

            <div style={styles.formActions}>
              <button
                type="button"
                onClick={() => {
                  setEditingProgram(null);
                  setFormData({ pgm_name: '', pgm_code: '', dept_id: '', duration_years: '' });
                }}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  ...styles.submitBtn,
                  ...(loading && styles.submitBtnDisabled),
                }}
              >
                {loading ? (
                  <>
                    <div style={styles.spinner} />
                    Saving...
                  </>
                ) : editingProgram ? (
                  'Update Program'
                ) : (
                  'Create Program'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Programs Table */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>
              <BookOpen size={24} />
              Programs List ({filteredPrograms.length})
            </h2>
            <div style={styles.searchContainer}>
              <Search size={20} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search programs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>
          </div>

          <div style={styles.tableContainer}>
            {filteredPrograms.length === 0 ? (
              <div style={styles.emptyState}>
                <BookOpen size={64} style={{ color: '#9ca3af' }} />
                <h3 style={styles.emptyTitle}>No programs found</h3>
                <p style={styles.emptySubtitle}>Create your first program to get started</p>
              </div>
            ) : (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeadRow}>
                      <th style={styles.tableHead}>Program Name</th>
                      <th style={styles.tableHead}>Program Code</th>
                      <th style={styles.tableHead}>Department</th>
                      <th style={styles.tableHead}>Duration (Years)</th>
                      <th style={styles.tableHead}>Program ID</th>
                      <th style={styles.tableHead}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPrograms.map((prog) => (
                      <tr key={prog.pgm_id} style={styles.tableRow}>
                        <td style={styles.tableCell}>
                          <div style={styles.cellContent}>
                            <div style={styles.progName}>{prog.pgm_name}</div>
                          </div>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={styles.progCodeChip}>{prog.pgm_code}</div>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={styles.deptChip}>{prog.dept_name || 'Unassigned'}</div>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={styles.durationChip}>{prog.duration_years}</div>
                        </td>
                        <td style={styles.tableCell}>
                          <code style={styles.progId}>{prog.pgm_id}</code>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={styles.actionButtons}>
                            <button
                              onClick={() => handleEdit(prog)}
                              style={styles.editBtn}
                              title="Edit"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(prog.pgm_id)}
                              style={styles.deleteBtn}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────
// Styles (consistent with AcademicYearManagement)
const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '32px',
    background: '#f8fafc',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  contentWrapper: {
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  iconBg: {
    width: '56px',
    height: '56px',
    background: 'linear-gradient(135deg, #22c55e, #15803d)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerIcon: {
    color: 'white',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
  },
  subtitle: {
    color: '#64748b',
    fontSize: '16px',
    margin: 0,
  },
  newBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #22c55e, #15803d)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '15px',
  },
  card: {
    background: '#fff',
    borderRadius: '20px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    marginBottom: '32px',
  },
  cardHeader: {
    padding: '28px 32px',
    borderBottom: '1px solid #f1f5f9',
  },
  cardTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  message: {
    padding: '16px 24px',
    borderRadius: '12px',
    margin: '0 32px 24px 32px',
    fontWeight: '500',
  },
  successMsg: {
    background: 'linear-gradient(90deg, #dcfce7, #bbf7d0)',
    color: '#166534',
    border: '1px solid #86efac',
  },
  errorMsg: {
    background: 'linear-gradient(90deg, #fee2e2, #fecaca)',
    color: '#dc2626',
    border: '1px solid #fca5a5',
  },
  form: {
    padding: '32px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    marginBottom: '32px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    padding: '14px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
  },
  select: {
    padding: '14px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
  },
  formActions: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    padding: '10px 20px',
    background: '#f8fafc',
    color: '#64748b',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  submitBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '15px',
  },
  submitBtnDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  searchContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    maxWidth: '300px',
  },
  searchIcon: {
    position: 'absolute',
    left: '16px',
    color: '#9ca3af',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '12px 16px 12px 48px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
  },
  tableContainer: {
    padding: '0',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeadRow: {
    background: '#f8fafc',
  },
  tableHead: {
    padding: '20px 24px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '700',
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  tableRow: {
    borderBottom: '1px solid #f1f5f9',
    transition: 'all 0.2s ease',
  },
  tableCell: {
    padding: '20px 24px',
  },
  cellContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  progName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
  },
  progCodeChip: {
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
    color: '#065f46',
    borderRadius: '999px',
    fontSize: '14px',
    fontWeight: '500',
    display: 'inline-block',
  },
  deptChip: {
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    color: '#1e40af',
    borderRadius: '999px',
    fontSize: '14px',
    fontWeight: '500',
    display: 'inline-block',
  },
  durationChip: {
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
    color: '#3730a3',
    borderRadius: '999px',
    fontSize: '14px',
    fontWeight: '500',
    display: 'inline-block',
  },
  progId: {
    background: '#f3f4f6',
    color: '#6b7280',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontFamily: 'monospace',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  editBtn: {
    padding: '10px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    border: 'none',
    cursor: 'pointer',
    color: '#1e40af',
    transition: 'all 0.2s ease',
  },
  deleteBtn: {
    padding: '10px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
    border: 'none',
    cursor: 'pointer',
    color: '#dc2626',
    transition: 'all 0.2s ease',
  },
  emptyState: {
    textAlign: 'center',
    padding: '80px 40px',
    color: '#6b7280',
  },
  emptyTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#374151',
    margin: '16px 0 8px 0',
  },
  emptySubtitle: {
    fontSize: '16px',
    margin: 0,
  },
  spinner: {
    width: 18,
    height: 18,
    border: '2px solid transparent',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

// Global animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin { to { transform: rotate(360deg); } }
  tr:hover { background: #f8fafc !important; }
  input[type="number"]::-webkit-inner-spin-button { opacity: 1; }
`;
document.head.appendChild(styleSheet);

export default ProgramManagement;
